import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';
import { useSurveyUiToast } from '../hooks/useSurveyUiToast';
import { useLocation, useParams } from 'react-router-dom';
import { useOktaAuth } from '@okta/okta-react';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import {
  getApimSubscriptionKey,
  getRuntimeApiBaseUrl,
} from '../../services/platformGateway';
import { getPublicM2MToken } from '../../services/publicTokenService';
import { SurveyFields, buildSubmitPayload } from '../components/SurveyFields';
import SurveyProgress from '../components/SurveyProgress';
import { computeVisiblePath, computeVisibleSections } from '../utils/branchingPath';
import '../styles/surveyModule.css';

/**
 * Secure (restricted) share links carry `?access=secure` (see
 * buildSurveyPublicUrl). It lets the page know up front that the respondent
 * must sign in, so it never touches the anonymous public-token API for them.
 */
const SECURE_LINK_PARAM = 'access';
const SECURE_LINK_VALUE = 'secure';

/**
 * Returns the M2M bearer token for anonymous APIM access.
 * Fetched from the SWA built-in API (/api/public-token) which mints the token server-side
 * and caches it in Redis — all respondents share one token, no Okta rate-limit risk.
 * Returns null in local dev (SWA API not running) so direct runtime calls still work.
 *
 * Only used for anonymous respondents on public links. Signed-in respondents
 * (always the case for secure surveys) use their own Okta access token instead.
 */
async function getPublicAccessToken() {
  try {
    return await getPublicM2MToken();
  } catch {
    return null;
  }
}

/**
 * Restricted (secure) surveys: AUTH_REQUIRED (401) means "sign in with Okta
 * and retry with X-Id-Token"; NOT_INVITED (403) means the signed-in user is
 * not on the invite list; ALREADY_RESPONDED (409) means this respondent has
 * already answered via this link. All are surfaced via err.accessCode.
 *
 * `bearer` is the APIM bearer token: the respondent's own Okta access token
 * when signed in, otherwise the shared anonymous M2M token (public links only).
 */
async function publicFetchJson(path, opts = {}, idToken = null, bearer = null) {
  const url = `${getRuntimeApiBaseUrl()}${path}`;
  const headers = { 'Content-Type': 'application/json', ...(opts.headers || {}) };
  const subsKey = getApimSubscriptionKey();
  if (subsKey) headers['Subscription-Key'] = subsKey;
  if (bearer) headers.Authorization = `Bearer ${bearer}`;
  // Identity for restricted surveys; ignored by the backend for public ones.
  if (idToken) headers['X-Id-Token'] = idToken;
  const res = await fetch(url, { ...opts, headers });
  const text = await res.text();
  if (!res.ok) {
    let detail = '';
    try {
      detail = JSON.parse(text)?.detail || '';
    } catch {
      detail = '';
    }
    const err = new Error(text || res.statusText || 'Request failed');
    err.status = res.status;
    if (detail === 'AUTH_REQUIRED' || detail === 'NOT_INVITED' || detail === 'ALREADY_RESPONDED') {
      err.accessCode = detail;
    }
    throw err;
  }
  if (res.status === 204) return null;
  return text ? JSON.parse(text) : null;
}

/**
 * Secure (restricted) links: pre-fill the builder's "collect respondent
 * details" questions (marked with config.profile) from the signed-in user's
 * verified identity. Fields stay editable — this only saves typing.
 */
function buildRespondentPrefill(surveyData) {
  if (surveyData?.access_mode !== 'restricted') return {};
  const prefill = {};
  (surveyData.sections || []).forEach((sec) => {
    (sec.questions || []).forEach((q) => {
      const profile = q?.config?.profile;
      if (profile === 'respondent_email' && surveyData.respondent_email) {
        prefill[q.question_key] = surveyData.respondent_email;
      } else if (profile === 'respondent_name' && surveyData.respondent_name) {
        prefill[q.question_key] = surveyData.respondent_name;
      }
    });
  });
  return prefill;
}

function formatPublicDateTime(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

// Backend rejects an incomplete submit with a 400 whose body is
// {"detail":"MISSING_REQUIRED:key1,key2"} (see survey_public.py's
// _validate_answers) — this is a safety net for cases the client-side check
// in SurveyFields didn't catch. Turn that wire-format detail into a plain,
// user-facing sentence instead of surfacing the raw JSON/error-code string.
function describeSubmitError(raw) {
  let detail = raw;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.detail === 'string') detail = parsed.detail;
  } catch {
    // Not JSON (e.g. a network failure message) — use it as-is below.
  }
  if (detail.startsWith('MISSING_REQUIRED')) {
    return {
      isMissingRequired: true,
      message: "Some required questions haven't been answered yet. We've highlighted them — please look through each section and fill them in before submitting again.",
    };
  }
  return { isMissingRequired: false, message: detail || 'Something went wrong while submitting your response. Please try again.' };
}

function publicSurveyClosedMessage(survey) {
  if (!survey) return 'This survey is closed and is no longer accepting responses.';
  if (survey.state === 'not_open') {
    const opens = formatPublicDateTime(survey.opens_at_utc);
    if (opens) {
      return `This survey is not open yet. It will accept responses starting ${opens}.`;
    }
    return 'This survey is not open yet.';
  }
  const closedAt = formatPublicDateTime(survey.closes_at_utc);
  if (closedAt) {
    return `This survey closed on ${closedAt} and is no longer accepting responses.`;
  }
  return 'This survey is closed and is no longer accepting responses.';
}

/**
 * Public respondent survey page. Public links stay fully anonymous (M2M only).
 * Secure (restricted) links additionally require the respondent to sign in
 * with Okta — same login as the SDA app — and be on the link's invite list;
 * their ID token is sent as X-Id-Token so the runtime can verify identity.
 */
export default function SurveyPublicPage() {
  const { token } = useParams();
  const { search } = useLocation();
  const { oktaAuth, authState } = useOktaAuth();
  // Secure share links are tagged `?access=secure` so we can skip the anonymous
  // public-token round-trip entirely and go straight to Okta sign-in.
  const isSecureLink = new URLSearchParams(search).get(SECURE_LINK_PARAM) === SECURE_LINK_VALUE;
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [done, setDone] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [missingSignal, setMissingSignal] = useState(0);
  const [accessBlock, setAccessBlock] = useState(null);
  const { showError } = useSurveyUiToast();

  const isAuthenticated = !!authState?.isAuthenticated;

  const getIdToken = useCallback(async () => {
    if (!isAuthenticated) return null;
    try {
      const idTokenObj = await oktaAuth.tokenManager.get('idToken');
      return (
        (typeof idTokenObj?.idToken === 'string' && idTokenObj.idToken) ||
        (typeof idTokenObj?.value === 'string' && idTokenObj.value) ||
        authState?.idToken?.idToken ||
        null
      );
    } catch {
      return authState?.idToken?.idToken || null;
    }
  }, [isAuthenticated, oktaAuth, authState]);

  /**
   * APIM bearer token for this respondent.
   *  - Signed in (always true for a secure survey once past Okta): their own
   *    Okta access token — same as the rest of the app. No public-token call.
   *  - Anonymous on a secure link: null. The caller redirects to Okta instead
   *    of calling the API, so the public-token endpoint is never touched.
   *  - Anonymous on a public link: the shared M2M token from /api/public-token.
   */
  const getBearerToken = useCallback(async () => {
    if (isAuthenticated) {
      try {
        const accessToken = await oktaAuth.getAccessToken();
        if (accessToken) return accessToken;
      } catch {
        // fall through
      }
    }
    // Secure links never fall back to the anonymous M2M token.
    if (isSecureLink) return null;
    return getPublicAccessToken();
  }, [isAuthenticated, isSecureLink, oktaAuth]);

  const signInAndReturn = useCallback(() => {
    void oktaAuth.signInWithRedirect({
      originalUri: `${window.location.pathname}${window.location.search}`,
    });
  }, [oktaAuth]);
  const visibleQuestionKeys = computeVisiblePath(survey, answers);
  const questionCount = (survey?.sections || []).reduce(
    (acc, sec) => acc + (sec.questions || []).filter((q) => visibleQuestionKeys.has(q.question_key)).length,
    0,
  );
  // Only count real author-created sections (tab_label set) — a flat survey's
  // implicit single "Survey" wrapper isn't a section from the respondent's
  // view — and skip sections hidden by section-level display logic.
  const visibleSectionIdx = computeVisibleSections(survey, answers);
  const sectionCount = (survey?.sections || []).filter(
    (sec, i) => sec.tab_label && visibleSectionIdx.has(i),
  ).length;

  useEffect(() => {
    if (!token) return undefined;
    // Wait for Okta to finish restoring any existing session so a restricted
    // link doesn't bounce an already-signed-in user to the login screen.
    if (!authState) return undefined;
    let cancelled = false;
    let redirecting = false;
    setLoading(true);
    // Secure link + no session: go straight to Okta. No API call, and in
    // particular no anonymous public-token request, is made for this link.
    if (isSecureLink && !isAuthenticated) {
      signInAndReturn();
      return undefined;
    }
    void (async () => {
      try {
        const [idToken, bearer] = await Promise.all([getIdToken(), getBearerToken()]);
        const data = await publicFetchJson(
          `/public/surveys/${encodeURIComponent(token)}`,
          {},
          idToken,
          bearer,
        );
        if (!cancelled) {
          setSurvey(data);
          // Keep anything the respondent already typed (e.g. token refresh
          // mid-survey re-runs this effect) — prefill only fills gaps.
          const prefill = buildRespondentPrefill(data);
          setAnswers((prev) => ({ ...prefill, ...prev }));
          setAccessBlock(null);
          setErr(null);
        }
      } catch (e) {
        if (cancelled) return;
        if (e?.accessCode === 'AUTH_REQUIRED') {
          if (!isAuthenticated) {
            // Secured link, no session: go straight to Okta — no interstitial.
            // signInWithRedirect leaves the page and returns to this URL after
            // login, so just keep the spinner up while the redirect happens.
            redirecting = true;
            signInAndReturn();
            return;
          }
          // Signed in but the server still rejected the token (e.g. expired
          // mid-restore) — show the manual sign-in card instead of looping.
          setAccessBlock('auth_required');
        } else if (e?.accessCode === 'NOT_INVITED') {
          setAccessBlock('not_invited');
        } else if (e?.accessCode === 'ALREADY_RESPONDED') {
          setAccessBlock('already_responded');
        } else {
          setErr(e instanceof Error ? e.message : 'Failed to load survey');
        }
      } finally {
        if (!cancelled && !redirecting) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, authState, isAuthenticated, isSecureLink, getIdToken, getBearerToken, signInAndReturn]);

  const onSubmit = useCallback(
    async () => {
      if (!token || !survey) return;
      // Use the shared serializer so rating follow-up reasons and low-rating
      // issue selections are captured. The previous inline logic sent the rating
      // as a plain score string and silently dropped those sub-answers, so the
      // "What made this a low rating?" chips were never stored.
      const flat = buildSubmitPayload(survey, answers);
      setSubmitting(true);
      setErr(null);
      try {
        const [idToken, bearer] = await Promise.all([getIdToken(), getBearerToken()]);
        const out = await publicFetchJson(
          `/public/surveys/${encodeURIComponent(token)}/responses`,
          {
            method: 'POST',
            body: JSON.stringify({ answers: flat }),
          },
          idToken,
          bearer,
        );
        setDone(out);
      } catch (e) {
        if (e?.accessCode === 'AUTH_REQUIRED') {
          setAccessBlock('auth_required');
        } else if (e?.accessCode === 'NOT_INVITED') {
          setAccessBlock('not_invited');
        } else if (e?.accessCode === 'ALREADY_RESPONDED') {
          setAccessBlock('already_responded');
        } else {
          const raw = e instanceof Error ? e.message : 'Submit failed';
          const { isMissingRequired, message } = describeSubmitError(raw);
          if (isMissingRequired) {
            showError("Please fill all required questions.");
            setMissingSignal((n) => n + 1);
          } else {
            setErr(message);
          }
        }
      } finally {
        setSubmitting(false);
      }
    },
    [answers, survey, token, getIdToken, getBearerToken],
  );

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Fallback only: unauthenticated visitors are redirected straight to Okta
  // by the load effect above. This card appears when a signed-in session was
  // rejected by the server (e.g. expired mid-survey) so we don't redirect-loop.
  if (accessBlock === 'auth_required') {
    return (
      <Box className="survey-module survey-public-page" sx={{ p: { xs: 2, sm: 3 }, maxWidth: 560, width: '100%' }}>
        <div className="public-survey-card" style={{ textAlign: 'center' }}>
          <LockOutlinedIcon sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
            This survey is private
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            It was shared with specific people only. Sign in with your JLL account to
            confirm it&apos;s you.
          </Typography>
          <Button variant="contained" onClick={signInAndReturn}>
            Sign in to continue
          </Button>
        </div>
      </Box>
    );
  }

  if (accessBlock === 'not_invited') {
    return (
      <Box className="survey-module survey-public-page" sx={{ p: { xs: 2, sm: 3 }, maxWidth: 560, width: '100%' }}>
        <div className="public-survey-card" style={{ textAlign: 'center' }}>
          <LockOutlinedIcon sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            You don&apos;t have access to this survey
          </Typography>
        </div>
      </Box>
    );
  }

  // Restricted links accept one response per invited respondent. The server
  // refuses both the survey load and the submit with ALREADY_RESPONDED, so the
  // questions are never shown again once this person has answered.
  if (accessBlock === 'already_responded') {
    return (
      <Box className="survey-module survey-public-page" sx={{ p: { xs: 2, sm: 3 }, maxWidth: 560, width: '100%' }}>
        <div className="public-survey-card" style={{ textAlign: 'center' }} role="dialog" aria-labelledby="already-responded-title">
          <TaskAltOutlinedIcon sx={{ fontSize: 40, mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            This survey accepts one response per person, and yours has been recorded. Thank you.
          </Typography>
        </div>
      </Box>
    );
  }

  if (err && !survey) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{err}</Alert>
      </Box>
    );
  }

  if (done) {
    return (
      <Box className="survey-module survey-public-page" sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1120, width: '100%' }}>
        <div className="public-survey-card public-survey-success">
          <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
            Thank you for your response
          </Typography>
          <Typography variant="body2" color="text.secondary" className="public-survey-success-copy">
            Your response has been recorded (reference #{done.response_id}).
          </Typography>
        </div>
      </Box>
    );
  }

  return (
    <Box
      className="survey-module survey-public-page"
      sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1200, width: '100%' }}
    >
      {/* Banner with overlaid logo */}
      <div className={`public-survey-hero ${survey?.banner_image ? 'has-banner' : 'no-banner'}`}>
        {survey?.banner_image && (
          <div className="public-survey-banner">
            <img
              src={survey.banner_image}
              alt=""
              style={{
                objectPosition: `${survey.banner_position?.x ?? 50}% ${survey.banner_position?.y ?? 50}%`,
              }}
            />
          </div>
        )}
        {survey?.logo_image && (
          <div className="public-survey-logo-wrapper">
            <img src={survey.logo_image} alt="" className="public-survey-logo" />
          </div>
        )}
      </div>

      {/* Title and description below banner */}
      <header className="public-survey-header">
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          {survey?.title}
        </Typography>
        <Typography variant="body1" color="text.secondary" className="public-survey-lede">
          {survey?.description?.trim() || 'Your feedback helps us improve the experience.'}
        </Typography>
        {survey?.state === 'open' && survey?.closes_at_utc && (
          <Typography variant="body2" color="text.secondary" className="public-survey-closes-note">
            Accepting responses until {formatPublicDateTime(survey.closes_at_utc)}.
          </Typography>
        )}
        {survey?.state === 'open' && (
          <div className="public-survey-meta">
            {questionCount > 0 && <span>{questionCount} questions</span>}
            {sectionCount > 0 && <span>{sectionCount} sections</span>}
            <span>Fields marked with * are required.</span>
          </div>
        )}
        {survey?.access_mode === 'restricted' && survey?.respondent_email && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 0.5, display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            <LockOutlinedIcon sx={{ fontSize: 16 }} />
            Private survey — responding as {survey.respondent_email}
          </Typography>
        )}
      </header>

      {survey?.state && survey.state !== 'open' ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {publicSurveyClosedMessage(survey)}
        </Alert>
      ) : (
        <>
          <SurveyProgress survey={survey} answers={answers} />
          <div className="preview-card public-survey-card">
            {survey && (
              <SurveyFields
                survey={survey}
                answers={answers}
                setAnswers={setAnswers}
                onSubmit={onSubmit}
                submitting={submitting}
                submitError={err}
                highlightMissingSignal={missingSignal}
              />
            )}
          </div>
        </>
      )}
    </Box>
  );
}
